import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { Copy, Send, Check, Clock, UserPlus, Users, Link2, RefreshCw } from 'lucide-react'
import Modal from './Modal'
import { useToast } from '../hooks/useToast'

/**
 * SpaceInviteModal Component
 * Discord-quality invite system with:
 * - Share link with copy button
 * - Expiration settings (Never, 1hr, 1day, 7days)
 * - Max uses settings (Unlimited, 1, 5, 10)
 * - Invite friends directly
 * - Show space members
 * - Active invites management
 */
export default function SpaceInviteModal({ isOpen, onClose, space, userId }) {
  const toast = useToast()
  const [friends, setFriends] = useState([])
  const [spaceMembers, setSpaceMembers] = useState([])
  const [sentInvites, setSentInvites] = useState({}) // Track which friends have been invited
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('link') // link, friends, members
  const [linkSettings, setLinkSettings] = useState({
    expires_in: 'never', // '1hour', '1day', '7days', 'never'
    max_uses: 'unlimited', // '1', '5', '10', 'unlimited'
  })

  useEffect(() => {
    if (isOpen && userId && space) {
      loadSpaceMembers()
      loadSentInvites()
    }
  }, [isOpen, userId, space])

  const loadSpaceMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('space_members')
        .select(`
          id,
          role,
          joined_at,
          profiles:user_id (
            id,
            username,
            avatar_url,
            custom_status
          )
        `)
        .eq('space_id', space.id)
        .order('joined_at', { ascending: false })

      if (error) throw error
      setSpaceMembers(data || [])

      // After loading members, load friends not in space
      loadFriends(data || [])
    } catch (error) {
      console.error('Error loading space members:', error)
    }
  }

  const loadFriends = async (members) => {
    try {
      // Load accepted friendships
      const { data, error } = await supabase
        .from('friendships')
        .select('*, profiles!friendships_friend_id_fkey(id, username, avatar_url, custom_status)')
        .eq('user_id', userId)
        .eq('status', 'accepted')

      if (!error && data) {
        // Filter out friends who are already members
        const memberIds = members.map(m => m.profiles.id)
        const friendsNotInSpace = data.filter(f => !memberIds.includes(f.profiles.id))
        setFriends(friendsNotInSpace)
      }
    } catch (error) {
      console.error('Error loading friends:', error)
    }
  }

  const loadSentInvites = async () => {
    if (!space?.id) return

    try {
      // Load pending invites for this space
      const { data, error } = await supabase
        .from('space_invites')
        .select('invited_user_id, status')
        .eq('space_id', space.id)
        .eq('created_by', userId)
        .in('status', ['pending', 'accepted'])

      if (!error && data) {
        const inviteMap = {}
        data.forEach(invite => {
          inviteMap[invite.invited_user_id] = invite.status
        })
        setSentInvites(inviteMap)
      }
    } catch (error) {
      console.error('Error loading sent invites:', error)
    }
  }

  const getInviteUrl = () => {
    const baseUrl = window.location.origin
    return `${baseUrl}/invite/${space?.invite_code || 'LOADING'}`
  }

  const copyInviteLink = () => {
    if (!space?.invite_code) {
      toast.error('No invite code available')
      return
    }

    navigator.clipboard.writeText(getInviteUrl())
    toast.success('Invite link copied to clipboard!')
  }

  const copyInviteCode = () => {
    if (!space?.invite_code) {
      toast.error('No invite code available')
      return
    }

    navigator.clipboard.writeText(space.invite_code)
    toast.success('Invite code copied!')
  }

  const generateNewInvite = async () => {
    setLoading(true)
    try {
      // Generate new invite code
      const newInviteCode = Math.random().toString(36).substring(2, 10).toUpperCase()

      // Calculate expiration
      let expiresAt = null
      if (linkSettings.expires_in !== 'never') {
        const now = new Date()
        const expiryMap = {
          '1hour': 60 * 60 * 1000,
          '1day': 24 * 60 * 60 * 1000,
          '7days': 7 * 24 * 60 * 60 * 1000
        }
        expiresAt = new Date(now.getTime() + expiryMap[linkSettings.expires_in])
      }

      // Get max uses
      const maxUses = linkSettings.max_uses === 'unlimited' ? null : parseInt(linkSettings.max_uses)

      // Update space invite settings
      const { error } = await supabase
        .from('spaces')
        .update({
          invite_code: newInviteCode,
          invite_expires_at: expiresAt,
          invite_max_uses: maxUses
        })
        .eq('id', space.id)

      if (error) throw error

      // Update local space object
      space.invite_code = newInviteCode

      toast.success('New invite link generated!')
    } catch (error) {
      console.error('Error generating invite:', error)
      toast.error('Failed to generate new invite link')
    } finally {
      setLoading(false)
    }
  }

  const sendDirectInvite = async (friend) => {
    setLoading(true)
    try {
      // Create space invite record
      const { error: inviteError } = await supabase
        .from('space_invites')
        .insert({
          space_id: space.id,
          created_by: userId,
          invited_user_id: friend.profiles.id,
          status: 'pending',
        })

      if (inviteError) throw inviteError

      // Create notification
      try {
        await supabase.from('notifications').insert({
          user_id: friend.profiles.id,
          type: 'space_invite',
          title: 'Space Invitation',
          message: `You've been invited to join ${space.name}`,
          action_url: `/spaces/${space.id}`,
        })
      } catch (notifError) {
        console.log('Could not create notification:', notifError)
      }

      // Update sent invites state
      setSentInvites(prev => ({ ...prev, [friend.profiles.id]: 'pending' }))
      toast.success(`Invite sent to @${friend.profiles.username}!`)
    } catch (error) {
      console.error('Error sending invite:', error)
      toast.error('Failed to send invite')
    } finally {
      setLoading(false)
    }
  }

  const rescindInvite = async (friendId) => {
    setLoading(true)
    try {
      const { error } = await supabase
        .from('space_invites')
        .delete()
        .eq('space_id', space.id)
        .eq('created_by', userId)
        .eq('invited_user_id', friendId)
        .eq('status', 'pending')

      if (error) throw error

      // Update state
      setSentInvites(prev => {
        const newState = { ...prev }
        delete newState[friendId]
        return newState
      })
      toast.info('Invite rescinded')
    } catch (error) {
      console.error('Error rescinding invite:', error)
      toast.error('Failed to rescind invite')
    } finally {
      setLoading(false)
    }
  }

  const getExpirationLabel = () => {
    const labels = {
      'never': 'Never',
      '1hour': '1 Hour',
      '1day': '1 Day',
      '7days': '7 Days'
    }
    return labels[linkSettings.expires_in] || 'Never'
  }

  const getMaxUsesLabel = () => {
    const labels = {
      'unlimited': 'Unlimited',
      '1': '1 Use',
      '5': '5 Uses',
      '10': '10 Uses'
    }
    return labels[linkSettings.max_uses] || 'Unlimited'
  }

  const filteredFriends = friends.filter(friend =>
    friend.profiles.username.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (!isOpen || !space) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Invite to ${space.name}`} size="large">
      <div className="flex flex-col h-full">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gjyl-slate-700 pb-2">
          <button
            onClick={() => setActiveTab('link')}
            className={`px-4 py-2 rounded-t-gjyl transition-all flex items-center gap-2 ${
              activeTab === 'link'
                ? 'bg-gjyl-indigo-500 text-white'
                : 'glass text-slate-400 hover:text-white'
            }`}
          >
            <Link2 className="w-4 h-4" />
            <span>Share Link</span>
          </button>
          <button
            onClick={() => setActiveTab('friends')}
            className={`px-4 py-2 rounded-t-gjyl transition-all flex items-center gap-2 ${
              activeTab === 'friends'
                ? 'bg-gjyl-indigo-500 text-white'
                : 'glass text-slate-400 hover:text-white'
            }`}
          >
            <UserPlus className="w-4 h-4" />
            <span>Invite Friends</span>
            {friends.length > 0 && (
              <span className="px-1.5 py-0.5 bg-gjyl-indigo-400/30 rounded text-xs">
                {friends.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 rounded-t-gjyl transition-all flex items-center gap-2 ${
              activeTab === 'members'
                ? 'bg-gjyl-indigo-500 text-white'
                : 'glass text-slate-400 hover:text-white'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Members</span>
            <span className="px-1.5 py-0.5 bg-gjyl-slate-700 rounded text-xs">
              {spaceMembers.length}
            </span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-gjyl">
          {/* Share Link Tab */}
          {activeTab === 'link' && (
            <div className="space-y-6">
              {/* Current Invite Link */}
              <div className="glass rounded-gjyl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Link2 className="w-5 h-5 text-gjyl-indigo-400" />
                  <span className="text-sm font-semibold text-white">Share Invite Link</span>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="text"
                    value={getInviteUrl()}
                    readOnly
                    className="input-gjyl flex-1 text-sm font-mono bg-gjyl-slate-800/50 text-gjyl-indigo-300"
                  />
                  <button
                    onClick={copyInviteLink}
                    className="px-4 py-2 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-400 rounded-gjyl text-white transition-all hover:scale-105 flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </button>
                </div>

                <p className="text-xs text-slate-400">
                  Share this link with anyone you want to invite to this space. The link will work according to the settings below.
                </p>
              </div>

              {/* Invite Settings */}
              <div className="glass rounded-gjyl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-gjyl-indigo-400" />
                  <span className="text-sm font-semibold text-white">Invite Settings</span>
                </div>

                <div className="space-y-4">
                  {/* Expiration */}
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-2">
                      Expire After
                    </label>
                    <select
                      value={linkSettings.expires_in}
                      onChange={(e) => setLinkSettings({ ...linkSettings, expires_in: e.target.value })}
                      className="input-gjyl text-sm w-full bg-gjyl-slate-800"
                    >
                      <option value="never">Never</option>
                      <option value="1hour">1 Hour</option>
                      <option value="1day">1 Day</option>
                      <option value="7days">7 Days</option>
                    </select>
                  </div>

                  {/* Max Uses */}
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-2">
                      Max Number of Uses
                    </label>
                    <select
                      value={linkSettings.max_uses}
                      onChange={(e) => setLinkSettings({ ...linkSettings, max_uses: e.target.value })}
                      className="input-gjyl text-sm w-full bg-gjyl-slate-800"
                    >
                      <option value="unlimited">No Limit</option>
                      <option value="1">1 Use</option>
                      <option value="5">5 Uses</option>
                      <option value="10">10 Uses</option>
                    </select>
                  </div>

                  {/* Summary */}
                  <div className="bg-gjyl-slate-800/50 rounded-gjyl p-3 border border-gjyl-slate-700">
                    <div className="text-xs text-slate-400 mb-2">Current Settings:</div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gjyl-indigo-400" />
                        <span className="text-white">{getExpirationLabel()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gjyl-indigo-400" />
                        <span className="text-white">{getMaxUsesLabel()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Generate New Link Button */}
                  <button
                    onClick={generateNewInvite}
                    disabled={loading}
                    className="w-full px-4 py-2.5 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-400 rounded-gjyl text-white font-medium transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    <span>{loading ? 'Generating...' : 'Generate New Link'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Invite Friends Tab */}
          {activeTab === 'friends' && (
            <div className="space-y-4">
              {/* Search Friends */}
              {friends.length > 0 && (
                <div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search your friends..."
                    className="input-gjyl text-sm w-full"
                  />
                </div>
              )}

              {/* Friends List */}
              <div className="space-y-2">
                {filteredFriends.length === 0 ? (
                  <div className="glass rounded-gjyl p-8 text-center">
                    <UserPlus className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-sm text-slate-400 mb-2">
                      {friends.length === 0
                        ? 'All your friends are already in this space!'
                        : 'No friends found matching your search'}
                    </p>
                    {friends.length === 0 && (
                      <p className="text-xs text-slate-500">
                        Share the invite link from the "Share Link" tab to invite others
                      </p>
                    )}
                  </div>
                ) : (
                  filteredFriends.map((friend) => {
                    const inviteStatus = sentInvites[friend.profiles.id]
                    return (
                      <div
                        key={friend.id}
                        className="glass hover:glass-strong rounded-gjyl p-3 flex items-center gap-3 transition-all group"
                      >
                        <img
                          src={friend.profiles.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${friend.profiles.id}`}
                          alt={friend.profiles.username}
                          className="w-10 h-10 rounded-gjyl ring-2 ring-gjyl-slate-700 group-hover:ring-gjyl-indigo-500 transition-all"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate">
                            @{friend.profiles.username}
                          </div>
                          {friend.profiles.custom_status && (
                            <div className="text-xs text-slate-400 truncate">
                              {friend.profiles.custom_status}
                            </div>
                          )}
                        </div>

                        {/* Invite Status/Action */}
                        <div>
                          {inviteStatus === 'accepted' ? (
                            <div className="flex items-center gap-1 px-3 py-1 bg-green-500/10 border border-green-500/20 rounded text-xs text-green-400">
                              <Check className="w-3 h-3" />
                              <span>In Space</span>
                            </div>
                          ) : inviteStatus === 'pending' ? (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1 px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded text-xs text-yellow-400">
                                <Clock className="w-3 h-3" />
                                <span>Invited</span>
                              </div>
                              <button
                                onClick={() => rescindInvite(friend.profiles.id)}
                                disabled={loading}
                                className="px-2 py-1 hover:bg-red-500/20 rounded text-xs text-red-400 transition-all disabled:opacity-50"
                                title="Cancel invite"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => sendDirectInvite(friend)}
                              disabled={loading}
                              className="flex items-center gap-1 px-3 py-1.5 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-400 rounded-gjyl text-white text-sm transition-all hover:scale-105 disabled:opacity-50"
                            >
                              <Send className="w-3.5 h-3.5" />
                              <span>Invite</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-slate-300">
                  <span className="font-semibold text-white">{spaceMembers.length}</span> member{spaceMembers.length !== 1 ? 's' : ''}
                </div>
              </div>

              <div className="space-y-2">
                {spaceMembers.map((member) => (
                  <div
                    key={member.id}
                    className="glass rounded-gjyl p-3 flex items-center gap-3"
                  >
                    <img
                      src={member.profiles.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${member.profiles.id}`}
                      alt={member.profiles.username}
                      className="w-10 h-10 rounded-gjyl ring-2 ring-gjyl-slate-700"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-white truncate">
                          @{member.profiles.username}
                        </div>
                        {member.role === 'owner' && (
                          <span className="px-2 py-0.5 bg-yellow-500/20 border border-yellow-500/30 rounded text-xs text-yellow-400">
                            Owner
                          </span>
                        )}
                        {member.role === 'admin' && (
                          <span className="px-2 py-0.5 bg-red-500/20 border border-red-500/30 rounded text-xs text-red-400">
                            Admin
                          </span>
                        )}
                        {member.role === 'moderator' && (
                          <span className="px-2 py-0.5 bg-blue-500/20 border border-blue-500/30 rounded text-xs text-blue-400">
                            Mod
                          </span>
                        )}
                      </div>
                      {member.profiles.custom_status && (
                        <div className="text-xs text-slate-400 truncate">
                          {member.profiles.custom_status}
                        </div>
                      )}
                      <div className="text-xs text-slate-500">
                        Joined {new Date(member.joined_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 mt-6 pt-4 border-t border-gjyl-slate-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 glass hover:glass-strong rounded-gjyl text-white transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}
